import { _imagesURL } from "../constants/API";

/**
 * Global In-Memory Image Cache & Preloader
 *
 * Tracks loaded image URLs across the entire SPA session so that components
 * like ProfileAvatar, ImageComponent, Feed posts, and NavBar never flash,
 * flicker, or display unnecessary shimmer loaders for already-downloaded images.
 */

const loadedImageUrls = new Set();
const cachedImageObjects = new Map();
const MAX_CACHED_OBJECTS = 250;

/**
 * Resolves any relative or CDN image path to its full normalized URL.
 */
export const resolveFullImageUrl = (src) => {
  if (!src || typeof src !== "string") return "";
  const trimmed = src.trim();
  if (
    !trimmed ||
    trimmed === "undefined" ||
    trimmed === "null" ||
    trimmed.startsWith("http://") ||
    trimmed.startsWith("https://") ||
    trimmed.startsWith("data:") ||
    trimmed.startsWith("blob:") ||
    trimmed.includes("placeholder_Awatar") ||
    trimmed.includes("assets/") ||
    trimmed.includes("@fs") ||
    trimmed.includes("node_modules")
  ) {
    return trimmed;
  }

  const base = (_imagesURL || "").trim().replace(/\/+$/, "");
  const path = trimmed.replace(/^\/+/, "");
  return base ? `${base}/${path}` : trimmed;
};

/**
 * Checks synchronously whether an image is already loaded and ready in memory.
 */
export const isImageCached = (src) => {
  if (!src) return false;
  const resolved = resolveFullImageUrl(src);
  if (!resolved) return false;

  if (
    resolved.startsWith("data:") ||
    resolved.startsWith("blob:") ||
    resolved.includes("placeholder_Awatar") ||
    loadedImageUrls.has(resolved) ||
    loadedImageUrls.has(src)
  ) {
    return true;
  }

  const existingObj = cachedImageObjects.get(resolved);
  if (existingObj && existingObj.complete && existingObj.naturalWidth > 0) {
    loadedImageUrls.add(resolved);
    return true;
  }

  return false;
};

/**
 * Records an image URL as fully loaded in the session cache.
 */
export const markImageLoaded = (src) => {
  if (!src) return;
  const resolved = resolveFullImageUrl(src);
  if (resolved) loadedImageUrls.add(resolved);
  if (typeof src === "string") loadedImageUrls.add(src);
};

/**
 * Preloads an image into browser memory cache.
 */
export const preloadImage = (src) => {
  if (!src || typeof src !== "string") return Promise.resolve("");
  const resolved = resolveFullImageUrl(src);
  if (!resolved) return Promise.resolve("");

  if (isImageCached(resolved)) {
    return Promise.resolve(resolved);
  }

  return new Promise((resolve) => {
    try {
      const img = new Image();
      img.decoding = "async";
      img.loading = "eager";

      img.onload = () => {
        markImageLoaded(resolved);
        if (cachedImageObjects.size >= MAX_CACHED_OBJECTS) {
          const oldestKey = cachedImageObjects.keys().next().value;
          cachedImageObjects.delete(oldestKey);
        }
        cachedImageObjects.set(resolved, img);
        resolve(resolved);
      };

      img.onerror = () => {
        resolve("");
      };

      img.src = resolved;

      if (img.complete && img.naturalWidth > 0) {
        markImageLoaded(resolved);
        cachedImageObjects.set(resolved, img);
        resolve(resolved);
      }
    } catch {
      resolve("");
    }
  });
};

/**
 * Preloads a batch of image URLs sequentially or in idle time.
 */
export const preloadImages = (srcList = []) => {
  if (!Array.isArray(srcList) || srcList.length === 0) return;
  const urls = srcList.filter(Boolean);

  const runPreload = () => {
    urls.forEach((url) => {
      preloadImage(url);
    });
  };

  if (typeof window !== "undefined" && "requestIdleCallback" in window) {
    window.requestIdleCallback(runPreload, { timeout: 1500 });
  } else {
    setTimeout(runPreload, 100);
  }
};

/**
 * Extracts and preloads all images from feed posts (avatars, post media, clinical images).
 */
export const preloadFeedImages = (posts = []) => {
  if (!Array.isArray(posts) || posts.length === 0) return;

  const urlsToPreload = [];

  posts.forEach((post) => {
    if (!post) return;

    // User avatar
    if (post.user?.image) {
      urlsToPreload.push(post.user.image);
    }
    // Repost user avatar
    if (post.repost_user?.image) {
      urlsToPreload.push(post.repost_user.image);
    }

    // Post media (array or single string)
    if (Array.isArray(post.media)) {
      post.media.forEach((m) => {
        if (typeof m === "string" && !m.endsWith(".mp4") && !m.endsWith(".webm")) {
          urlsToPreload.push(m);
        }
      });
    } else if (
      typeof post.media === "string" &&
      !post.media.endsWith(".mp4") &&
      !post.media.endsWith(".webm")
    ) {
      urlsToPreload.push(post.media);
    }

    if (post.image) {
      urlsToPreload.push(post.image);
    }

    // Clinical case study images
    if (Array.isArray(post.clinical_images)) {
      post.clinical_images.forEach((ci) => {
        if (ci?.image) urlsToPreload.push(ci.image);
      });
    }
  });

  preloadImages(urlsToPreload);
};

/**
 * Preloads advertisement banners
 */
export const preloadAdImages = (ads = []) => {
  if (!Array.isArray(ads) || ads.length === 0) return;
  const urls = [];
  ads.forEach((ad) => {
    const media = ad?.general_ad?.media || ad?.media;
    if (media && typeof media === "string") {
      urls.push(media);
    }
  });
  preloadImages(urls);
};
